import { ReactElement, useState } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Space } from 'common/components/antd/Space/Space';
import { EnhancedModal, Body, Footer, Header } from 'common/components/EnhancedModal/EnhancedModal';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { formatDateFromISO, titleCase } from 'common/utils/format.utils';
import { notifySuccess } from 'common/utils/notifier.utils';
import { useSetReconciliationStatusMutation } from 'domains/reconciliation/queries/reconciliation.query';
import {
  EReconciliationStatus,
  IReconciliationEntity,
} from 'domains/reconciliation/types/reconciliation.types';
import { SFooter } from './pay.modal.style';

type TProps = {
  isOpen: boolean;
  onCancelPayModalHandler: () => void;
  data: IReconciliationEntity;
};

const StatisticReconciliationPayModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelPayModalHandler,
    data,
  } = props;

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const statusMutation = useSetReconciliationStatusMutation();

  const setPaid = async (): Promise<void> => {
    setIsLoading(true);

    try {
      await statusMutation.mutateAsync({
        counterpartyGUID: data?.creditor.guid,
        reconGUID: data?.guid,
        status: EReconciliationStatus.Confirmed,
      });

      notifySuccess('reconciliation.statistic.modal.pay.notification.success');

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.ReconciliationQueryKeys].getCounterpartyList._def,
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.ReconciliationQueryKeys].getCounterpartyParticipants._def,
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.ReconciliationQueryKeys].getParticipants._def,
      });

      setIsLoading(false);
      onCancelPayModalHandler();
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  return (
    <EnhancedModal width={480} open={isOpen} onCancel={onCancelPayModalHandler}>
      <Header>{t('reconciliation.statistic.modal.pay.heading')}</Header>
        <Body>
          <Space
            size={16}
            direction="horizontal"
            style={{
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>{t('reconciliation.statistic.table.column.status.title')}</Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>
              {data?.status ? titleCase(data?.status) : ''}
            </Typography.Text>
          </Space>
          <Space
            size={16}
            direction="horizontal"
            style={{
              marginTop: 24,
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>{t('reconciliation.statistic.table.column.debtor.title')}</Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>{data?.debtor?.name || ''}</Typography.Text>
          </Space>
          <Space
            size={16}
            direction="horizontal"
            style={{
              marginTop: 24,
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>{t('reconciliation.statistic.table.column.amountReconciled.title')}</Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>{data?.calculations?.saldo || ''}</Typography.Text>
          </Space>
          <Space
            size={16}
            direction="horizontal"
            style={{
              marginTop: 24,
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>{t('reconciliation.statistic.table.column.created.title')}</Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>
              {data?.end_date ? formatDateFromISO(data.end_date) : ''}
            </Typography.Text>
          </Space>
        </Body>
        <Footer showBorderTop={false}>
          <SFooter>
            <Button
              width="50%"
              size="large"
              style={{
                marginRight: 12,
              }}
              onClick={onCancelPayModalHandler}
            >
              {t('reconciliation.statistic.modal.pay.button.close')}
            </Button>
            <Button loading={isLoading} width="50%" size="large" onClick={setPaid} type="primary">
              {t('reconciliation.statistic.modal.pay.button.paid')}
            </Button>
          </SFooter>
        </Footer>
    </EnhancedModal>
  );
};

export default StatisticReconciliationPayModal;
